import type {
  ConfirmSignInInput,
  SignInInput,
  SignInOutput,
  UpdatePasswordInput,
} from 'aws-amplify/auth';

import {
  confirmSignIn,
  fetchAuthSession,
  getCurrentUser,
  signIn,
  signOut,
  updatePassword,
} from 'aws-amplify/auth';

export interface CognitoUserInfo {
  email: string;
  franchise: string;
  siteId: string;
  role: string;
  orgId: string;
}

export class AWSAuthService {
  async getCurrentUserInfo(): Promise<CognitoUserInfo | null> {
    try {
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;

      if (!idToken) {return null;}

      // Parse JWT payload
      const payload = idToken.payload;
      const franchise = (payload['custom:franchise'] as string) || '';
      const siteId = (payload['custom:siteId'] as string) || '';

      return {
        email: (payload.email as string) || user.username,
        franchise,
        siteId,
        role: (payload['custom:role'] as string) || 'viewer',
        orgId: `${franchise}-${siteId}`,
      };
    } catch (error) {
      // Log with more detail for initialization timing issues
      if (error instanceof Error && error.message?.includes('Auth UserPool not configured')) {
        console.warn(
          '⚠️ AWS Cognito UserPool not yet initialized - this is normal during app startup',
        );
      } else {
        console.error('Failed to get current user:', error);
      }
      return null;
    }
  }

  async getIdToken(): Promise<string | null> {
    try {
      // First try to get from Amplify session
      const session = await fetchAuthSession();
      const amplifyToken = session.tokens?.idToken?.toString() || null;

      if (amplifyToken) {
        return amplifyToken;
      }

      // Fallback to stored tokens
      const storedToken = this.getStoredToken();
      if (storedToken) {
        console.log('Using stored token (session may be expired, consider re-login)');
        return storedToken;
      }

      return null;
    } catch (error) {
      console.error('Failed to get ID token from Amplify:', error);

      // Try stored token as final fallback
      const storedToken = this.getStoredToken();
      if (storedToken) {
        console.log('Using stored token after Amplify error');
        return storedToken;
      }

      return null;
    }
  }

  private getStoredToken(): string | null {
    // Check multiple storage locations for token
    // 1. Cookie
    const cookieToken = document.cookie
      .split('; ')
      .find(row => row.startsWith('idToken='))
      ?.split('=')[1];

    if (cookieToken && cookieToken !== 'null' && cookieToken !== 'undefined') {
      return cookieToken;
    }

    // 2. LocalStorage
    const localToken = localStorage.getItem('idToken');
    if (localToken && localToken !== 'null' && localToken !== 'undefined') {
      return localToken;
    }

    return null;
  }

  async signInUser(username: string, password: string): Promise<SignInOutput> {
    try {
      // Check if there's already a signed-in user and sign them out first
      try {
        const currentUser = await getCurrentUser();
        if (currentUser) {
          console.log('Signing out existing user before new sign-in');
          await signOut();
        }
      } catch {
        // No current user, proceed with sign-in
      }

      // Try Amplify first (works without client secret)
      const signInInput: SignInInput = {
        username,
        password,
      };
      return await signIn(signInInput);
    } catch (error: any) {
      // If it fails due to client secret, we need a different approach
      if (error.message?.includes('SECRET_HASH')) {
        throw new Error(
          'This app client requires a client secret. Please either:\n1. Remove the client secret from the Cognito app client in AWS Console\n2. Create a new app client without a secret\n3. Use a backend service for authentication', { cause: error },
        );
      }
      throw error;
    }
  }

  async confirmSignInWithNewPassword(newPassword: string): Promise<SignInOutput> {
    try {
      const input: ConfirmSignInInput = {
        challengeResponse: newPassword,
      };
      return await confirmSignIn(input);
    } catch (error: any) {
      console.error('Failed to confirm sign-in with new password:', error);
      throw error;
    }
  }

  async updateUserPassword(oldPassword: string, newPassword: string): Promise<void> {
    try {
      const input: UpdatePasswordInput = {
        oldPassword,
        newPassword,
      };
      await updatePassword(input);
    } catch (error: any) {
      console.error('Failed to update password:', error);
      throw error;
    }
  }

  async signOutUser(): Promise<void> {
    await signOut();
  }

  async isAuthenticated(): Promise<boolean> {
    try {
      await getCurrentUser();
      return true;
    } catch {
      return false;
    }
  }

  async refreshToken(): Promise<void> {
    try {
      await fetchAuthSession({ forceRefresh: true });
    } catch (error) {
      console.error('Failed to refresh token:', error);
      throw error;
    }
  }

  async getSession(): Promise<any> {
    try {
      const session = await fetchAuthSession();
      return {
        idToken: session.tokens?.idToken?.toString(),
        accessToken: session.tokens?.accessToken?.toString(),
        // Note: Amplify v6 doesn't expose refresh token directly for security
        refreshToken: null,
      };
    } catch (error) {
      console.error('Failed to get session:', error);
      return null;
    }
  }

  async validateStoredToken(): Promise<boolean> {
    const token = this.getStoredToken();
    if (!token) {return false;}

    try {
      // Decode the JWT to check expiration
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();

      // Token is valid if not expired (with 5 minute buffer)
      return exp > now + 5 * 60 * 1000;
    } catch (error) {
      console.error('Failed to validate token:', error);
      return false;
    }
  }
}

export const awsAuth = new AWSAuthService();
